/**
 * Composer: the draft is shared across the dock, the page and pop-outs (sync channel +
 * localStorage); attachments upload through the files API (upload intent → PUT → complete)
 * before the message is sent with their ids; Enter sends, Shift+Enter breaks the line.
 */
import { filesCompleteUpload, filesCreateUpload, isApiError } from '@oex/contracts';
import { Alert, Button, Textarea } from '@oex/ui';
import { Paperclip, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useT } from '../../../app/i18n';
import { useSendMessage } from '../api';
import { useDraft } from '../sync';

type Upload = { fileId: string; name: string };

async function upload(file: File): Promise<Upload> {
  const intent = await filesCreateUpload({
    purpose: 'message_attachment',
    originalName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  });
  const res = await fetch(intent.uploadUrl, {
    method: 'PUT',
    headers: intent.headers as Record<string, string>,
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
  await filesCompleteUpload(intent.fileId);
  return { fileId: intent.fileId, name: file.name };
}

export function Composer({ threadId, disabled }: { threadId: string; disabled?: boolean }) {
  const t = useT();
  const [draft, setDraft] = useDraft(threadId);
  const [files, setFiles] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useSendMessage();
  const input = useRef<HTMLInputElement>(null);

  const submit = () => {
    const body = draft.trim();
    if (!body && files.length === 0) return;
    setError(null);
    send.mutate(
      {
        id: threadId,
        data: {
          body,
          clientMessageId: crypto.randomUUID(),
          ...(files.length ? { attachmentFileIds: files.map((f) => f.fileId) } : {}),
        },
      },
      {
        onSuccess: () => {
          setDraft('');
          setFiles([]);
        },
        onError: (e) => {
          const rule = isApiError(e) ? (e.problem.rule ?? e.problem.code) : null;
          const text = rule ? t(`messaging.error.${rule}`) : '';
          setError(text && !text.startsWith('messaging.error.') ? text : t('messaging.error.generic'));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      {files.length ? (
        <ul className="flex flex-wrap gap-2">
          {files.map((f) => (
            <li
              key={f.fileId}
              className="inline-flex items-center gap-1 rounded-[2px] border border-border px-2 py-0.5 text-xs"
            >
              <Paperclip className="size-3" aria-hidden /> {f.name}
              <button
                type="button"
                aria-label={t('messaging.composer.removeAttachment')}
                onClick={() => setFiles((l) => l.filter((x) => x.fileId !== f.fileId))}
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2">
        <Textarea
          aria-label={t('messaging.composer.label')}
          placeholder={t('messaging.composer.placeholder')}
          className="min-h-10 flex-1"
          value={draft}
          disabled={disabled}
          maxLength={8000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <input
          ref={input}
          type="file"
          className="hidden"
          aria-label={t('messaging.composer.attach')}
          multiple
          onChange={async (e) => {
            const picked = Array.from(e.target.files ?? []).slice(0, 10 - files.length);
            e.target.value = '';
            if (!picked.length) return;
            setUploading(true);
            try {
              const done = await Promise.all(picked.map(upload));
              setFiles((l) => [...l, ...done]);
            } catch {
              setError(t('messaging.error.upload'));
            } finally {
              setUploading(false);
            }
          }}
        />
        <Button
          variant="secondary"
          size="md"
          disabled={disabled || uploading}
          loading={uploading}
          aria-label={t('messaging.composer.attach')}
          onClick={() => input.current?.click()}
        >
          <Paperclip className="size-4" aria-hidden />
        </Button>
        <Button
          onClick={submit}
          disabled={disabled || (!draft.trim() && files.length === 0)}
          loading={send.isPending}
          aria-label={t('messaging.composer.send')}
        >
          <Send className="size-4" aria-hidden />
        </Button>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
    </div>
  );
}
